import React from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: React.ReactNode;
  href?: string;
}

export const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-white hover:bg-brand-dark active:bg-brand-dark/90 shadow-sm hover:shadow transition-all cursor-pointer',
  secondary:
    'bg-white/80 text-ink border border-ink/15 hover:border-brand hover:text-brand hover:bg-white active:bg-white transition-all cursor-pointer',
  ghost:
    'text-muted hover:text-ink hover:bg-white/70 transition-colors cursor-pointer',
};

export const sizeStyles: Record<ButtonSize, string> = {
  sm: 'min-h-10 px-4 py-2 text-sm rounded-md',
  md: 'min-h-11 px-5 py-2.5 text-sm rounded-md',
  lg: 'min-h-12 px-6 py-3.5 text-base rounded-md',
};

const baseStyles =
  'inline-flex items-center justify-center gap-2 font-semibold touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';

export function buttonClassName(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  className = ''
) {
  return `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className = '', children, href, ...props }, ref) => {
    const classes = buttonClassName(variant, size, className);

    if (href) {
      return (
        <a href={href} className={classes}>
          {children}
        </a>
      );
    }

    return (
      <button ref={ref} className={classes} {...props}>
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
